import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  ExternalLink,
  InlineTextButton,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';

import css from './TopbarDesktop.module.css';

// Tutus are split by brand: "Custom" brand is custom-made, every other brand is catalog.
// Both tutu types are included because older listings still use "Classical-Tutu".
const TUTU_TYPES = 'Classical-Tutu,Tutus';
const CATALOG_BRANDS = costumeBrandOptions
  .map(o => o.values)
  .filter(v => v !== 'Custom')
  .join(',');

const topbarCostumeOptions = [
  { value: 'all', label: 'All Costumes', search: {} },
  { value: 'single', label: 'Single Costumes', search: { pub_listingType: 'Individual' } },
  {
    value: 'customTutus',
    label: 'Custom Tutus',
    search: { pub_type: TUTU_TYPES, pub_brand: 'Custom' },
  },
  { value: 'tutus', label: 'Tutus', search: { pub_type: TUTU_TYPES, pub_brand: CATALOG_BRANDS } },
  { value: 'lots', label: 'Costume Lots', search: { pub_listingType: 'Lot' } },
];
import IconBag from '../../../../components/IconBag/IconBag';
import IconHeart from '../../../../components/IconHeart/IconHeart';
import Select from 'react-select';
import { selectBookmarkCartCount } from '../../../../ducks/cart.duck';
import { createResourceLocatorString } from '../../../../util/routes';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { costumeBrandOptions } from '../../../../config/marketplace-custom-config';

const SignupLink = () => {
  return (
    <NamedLink id="signup-link" name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink id="login-link" name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const CartLink = () => {
  const cartCount = useSelector(selectBookmarkCartCount);
  const showBadge = cartCount > 0;
  const displayCount = cartCount > 99 ? '99+' : cartCount;

  return (
    <NamedLink id="cart-link" className={css.topbarLink} name="CartPage">
      <span className={css.topbarLinkLabel}>
        <span className={css.iconCart}>
          <IconBag />
        </span>
        {showBadge ? <span className={css.badge}>{displayCount}</span> : null}
      </span>
    </NamedLink>
  );
};

const FavLink = () => {
  return (
    <NamedLink id="favorites-link" className={css.topbarLink} name="FavoritesPage">
      <span className={css.topbarLinkLabel}>
        <span className={css.iconHeart}>
          <IconHeart />
        </span>
      </span>
    </NamedLink>
  );
};

const ShopLink = () => {
  return (
    <NamedLink id="shop-link" className={css.topbarLink} name="ShopPage">
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.shop" defaultMessage="Shop" />
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, intl, onLogout }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    const isOffersPage = page === 'OffersPage' && currentPage === 'OffersSentPage';
    return currentPage === page || isAccountSettingsPage || isInboxPage || isOffersPage
      ? css.currentPage
      : null;
  };

  return (
    <Menu skipFocusOnNavigation={true}>
      <MenuLabel
        id="profile-menu-label"
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.profileMenu' })}
      >
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="ManageListingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <FormattedMessage id="TopbarDesktop.yourListingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="InboxPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('InboxPage'))}
            name="InboxPage"
            params={{ tab: 'orders' }}
          >
            <FormattedMessage id="TopbarDesktop.purchasesAndSales" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="MessagesPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('MessagesPage'))}
            name="MessagesPage"
            params={{ tab: 'enquiry' }}
          >
            <FormattedMessage id="TopbarDesktop.messagesLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="OffersPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('OffersPage'))}
            name="OffersPage"
          >
            <FormattedMessage id="TopbarDesktop.offersLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="FavoritesPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('FavoritesPage'))}
            name="FavoritesPage"
          >
            <FormattedMessage id="TopbarDesktop.FavouritesPage" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    isDashboardHeader,
    viewport,
    isFullWidthHeader,
  } = props;
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const isMobileLayout = viewport?.width > 0 && viewport?.width < 1280;
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(
    rootClassName || isAuthenticated ? css.root : css.authRoot,
    className,
    {
      [css.dashboardHeader]: isDashboardHeader,
      [css.fullWidthHeader]: isFullWidthHeader,
    }
  );

  const shopLinkMaybe = <ShopLink />;
  const cartLinkMaybe = <CartLink />;
  const favLinkMaybe = <FavLink />;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      intl={intl}
      onLogout={onLogout}
    />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;
  const customSelectStyles = {
    control: (styles, { isFocused, isDisabled, isSelected }) => ({
      ...styles,
      backgroundColor: '#fff',
      border: 'none',
      borderRadius: '0px',
      boxShadow: 'none',
      color: '#111111',
      fontSize: '14px',
      lineHeight: '100%',
      letterSpacing: 0,
      height: '40px',
      paddingLeft: '0px',
      paddingRight: '0px',
      minHeight: '40px',
      margin: '0px',
      padding: '0px ',
      fontWeight: '500',
      position: 'relative',
      cursor: 'pointer !important',
    }),
    valueContainer: (styles, { isFocused, isDisabled, isSelected }) => ({
      ...styles,
      color: isSelected ? '#4a4a4a' : '#B2B2B2',
      height: '100%',
      padding: '0px',
      margin: '0px',
      fontWeight: '600',
    }),
    placeholder: (styles, { isFocused, isDisabled, isSelected }) => ({
      ...styles,
      color: '#111111',
      padding: '0px',
      margin: '0px',
      fontWeight: '500',
    }),
    indicatorsContainer: (styles, { isFocused, isDisabled, isSelected }) => ({
      ...styles,
      padding: '0px',
      margin: '0px',
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      padding: '0px',
      margin: '0px',
      transition: 'all .2s ease',
      transform: state.isFocused.menuIsOpen ? 'rotate(180deg)' : null,
    }),
    input: (styles, { isFocused, isDisabled, isSelected }) => ({
      ...styles,
      color: isSelected ? '#4a4a4a' : '#B2B2B2',
      height: '100%',
      padding: '0px',
      margin: '0px',
      fontWeight: '600',
    }),
    indicatorSeparator: styles => ({ display: 'none', padding: '0px', margin: '0px' }),
    option: (styles, { isFocused, isDisabled, isSelected }) => ({
      ...styles,
      color: '#4a4a4a',
      padding: '15px',
      fontSize: isMobileLayout ? '13px' : '15px',
      fontWeight: '600',
      lineHeight: '100%',
      letterSpacing: '-0.04em',
      cursor: 'pointer !important',
      backgroundColor: isDisabled ? null : isFocused ? '#eee' : isSelected ? '#FFF' : null,
      ':hover': {
        backgroundColor: '#eee',
        color: '#4a4a4a',
      },
    }),
    singleValue: (styles, { isFocused, isDisabled, isSelected }) => ({
      ...styles,
      color: '#111111',
      fontWeight: '500',
    }),
    menu: base => ({
      ...base,
      borderRadius: 0,
      margin: 0,
      backgroundColor: '#fff',
      borderRadius: '10px',
      overflow: 'hidden',
      color: '#4a4a4a',
      position: 'absolute',
      top: '100%',
      left: '0',
      right: '0',
      width: '100%',
      minWidth: '276px',
      padding: '0px',
      boxShadow:
        '0px 40px 64px -12px rgba(0, 0, 0, 0.08), 0px 0px 14px -4px rgba(0, 0, 0, 0.05), 0px 32px 48px -8px rgba(0, 0, 0, 0.1)',
    }),
    menuList: base => ({
      ...base,
      padding: '0px',
      backgroundColor: '#fff',
      fontSize: isMobileLayout ? '13px' : '15px',
      lineHeight: '100%',
      letterSpacing: '-0.04em',
      color: '#4a4a4a',
    }),
    dropdownIndicator: base => ({
      ...base,
      color: '#4a4a4a',
      padding: '0px',
    }),
  };
  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={css.searchBar}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div className={css.searchSpacer} />
  );

  // Category links are managed in Console (Content > Top bar); Costumes, Top brands and
  // Shop all are added after them here.
  const categoryLinks = customLinks || [];

  return (
    <nav
      className={classes}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
    >
      <div className={css.fixedWidthContainer}>
        {!isDashboardHeader && (
          <LinkedLogo
            id="logo-topbar-desktop"
            className={css.logoLink}
            layout="desktop"
            alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
            linkToExternalSite={config?.topbar?.logoLink}
          />
        )}
        {searchFormMaybe}
        <div className={css.rightNav}>
          <a rel="nofollow" className={css.sellNowButton} href="/l/new">
            <FormattedMessage id="TopbarDesktop.sellNow" />
          </a>
          {isAuthenticated ? (
            <NamedLink className={css.topbarLink} name="DashboardPage">
              <span className={css.topbarLinkLabel}>
                <FormattedMessage id="TopbarDesktop.dashboard" />
              </span>
            </NamedLink>
          ) : null}
          {favLinkMaybe}
          {cartLinkMaybe}
          {profileMenuMaybe}
          {loginLinkMaybe}
          {signupLinkMaybe}
        </div>
      </div>
      {!isDashboardHeader ? (
        <div
          className={css.categoryRow}
          aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.categories' })}
          role="navigation"
        >
          {categoryLinks.map((link, i) =>
            link.route ? (
              <NamedLink
                key={`${link.text}_${i}`}
                className={css.categoryLink}
                name={link.route.name}
                params={link.route.params}
                to={link.route.to}
              >
                {link.text}
              </NamedLink>
            ) : (
              <ExternalLink key={`${link.text}_${i}`} className={css.categoryLink} href={link.href}>
                {link.text}
              </ExternalLink>
            )
          )}
          <Select
            styles={customSelectStyles}
            // Always show the "Costumes" label rather than the last option picked
            value={null}
            onChange={o => {
              const search = { pub_category: 'costumes', ...o.search };
              history.push(
                createResourceLocatorString('SearchPage', routeConfiguration, {}, search)
              );
            }}
            options={topbarCostumeOptions}
            isSearchable={false}
            className={css.createListingSelect}
            placeholder={intl.formatMessage({ id: 'TopbarDesktop.costumesPlaceholder' })}
          />
          <NamedLink className={css.categoryLink} name="LandingPage" to={{ hash: '#collection' }}>
            <FormattedMessage id="TopbarDesktop.topBrands" />
          </NamedLink>
          <NamedLink className={css.categoryLink} name="SearchPage">
            <FormattedMessage id="TopbarDesktop.shopAll" />
          </NamedLink>
        </div>
      ) : null}
    </nav>
  );
};

export default TopbarDesktop;
